import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Play } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney, fileUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import VideoPlayerModal, { videoSources } from "@/components/shop/VideoPlayerModal";

let activeCardVideo = null;
const claimPlayback = (el) => {
  if (el && activeCardVideo && activeCardVideo !== el && !activeCardVideo.paused) {
    activeCardVideo.pause();
  }
  if (el) activeCardVideo = el;
};

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const img = (product.images || [])[0];
  const videoUrl = product.video_url || null;
  const [hoverPlay, setHoverPlay] = useState(false);
  const [vidFailed, setVidFailed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const hoverTimer = useRef(null);
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const pctOff = onSale ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;
  const saveAmt = onSale ? product.compare_at_price - product.price : 0;
  const outOfStock = product.fulfillment_type !== "order_in" && product.stock <= 0;
  const deal = product.bulk_deal;
  const colorCount = (product.colors || []).length;
  const isSpecial = product.fulfillment_type === "order_in";
  const leadDays = product.lead_time_days || 7;

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (colorCount > 0) {
      navigate(`/product/${product.slug}`);
      return;
    }
    addItem(product, 1, null);
    toast.success(`Added ${product.name} to cart`);
  };

  const startHover = () => {
    if (!videoUrl || vidFailed) return;
    hoverTimer.current = setTimeout(() => setHoverPlay(true), 250);
  };
  const endHover = () => {
    clearTimeout(hoverTimer.current);
    setHoverPlay(false);
  };

  const openVideo = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  return (
    <Link
      to={`/product/${product.slug}`}
      className="ipv-grid-card group flex flex-col"
      data-testid={`product-card-${product.id}`}
      onMouseEnter={startHover}
      onMouseLeave={endHover}
    >
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        {img ? (
          <img
            src={fileUrl(img)}
            alt={product.name}
            className="w-full h-full object-cover living-img"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
            No image
          </div>
        )}
        {/* Video slot — hover/tap plays the owner's MP4 promo, photo stays as fallback */}
        {videoUrl && hoverPlay && !vidFailed && (
          <video
            ref={claimPlayback}
            className="absolute inset-0 w-full h-full object-cover animate-[fadeIn_0.3s_ease]"
            muted
            loop
            autoPlay
            playsInline
            preload="none"
            data-testid={`card-video-${product.id}`}
          >
            {videoSources(videoUrl).map((s, i, arr) => (
              <source
                key={s.src}
                src={s.src}
                type={s.type}
                onError={i === arr.length - 1 ? () => setVidFailed(true) : undefined}
              />
            ))}
          </video>
        )}
        {videoUrl && !vidFailed && (
          <button
            onClick={openVideo}
            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#0A1E5E]/85 backdrop-blur-sm border border-[#F0B90B]/70 text-[#F0B90B] text-[11px] font-black pl-2.5 pr-3.5 py-1.5 hover:bg-[#F0B90B] hover:text-[#0A1E5E] transition-colors shadow-md [touch-action:manipulation]"
            data-testid={`card-video-btn-${product.id}`}
            aria-label="Watch product video"
          >
            <Play className="w-3 h-3 fill-current" /> Video
          </button>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          {product.ships_only ? (
            <Badge className="bg-[#0A1C40] hover:bg-[#0A1C40] text-[#FFC220] rounded-sm text-[10px] tracking-widest font-bold uppercase" data-testid="card-badge-ships">
              Ships to you
            </Badge>
          ) : isSpecial ? (
            <Badge className="bg-[#0A1C40] hover:bg-[#0A1C40] text-white rounded-sm text-[10px] tracking-widest font-bold uppercase" data-testid="card-badge-arrives-in-days" data-lead-time-days={leadDays}>
              Special order · ~{leadDays} days
            </Badge>
          ) : (
            !outOfStock && (
              <Badge className="bg-[#15803d] hover:bg-[#15803d] text-white rounded-sm text-[10px] tracking-widest font-bold uppercase" data-testid="card-badge-pickup-today">
                Available in store right now
              </Badge>
            )
          )}
          {product.gold_tag && (
            <Badge className="bg-[#FFC220] hover:bg-[#FFC220] text-[#0A1C40] rounded-sm text-[10px] tracking-widest font-black uppercase border border-[#C9971E]" data-testid="card-badge-gold-tag">
              ★ GOLD DEAL
            </Badge>
          )}
          {product.is_pallet && (
            <Badge className="bg-[#DC2626] hover:bg-[#DC2626] text-white rounded-sm text-[10px] tracking-widest font-bold uppercase">
              Wholesale
            </Badge>
          )}
          {onSale && (
            <Badge className="bg-[#DC2626] hover:bg-[#DC2626] text-white rounded-sm text-[10px] tracking-widest font-bold uppercase">
              -{pctOff}% OFF
            </Badge>
          )}
          {deal && deal.min_qty >= 2 && deal.percent_off > 0 && (
            <Badge className="bg-[#FFC220] hover:bg-[#FFC220] text-[#0A1C40] rounded-sm text-[10px] tracking-widest font-bold uppercase">
              Buy {deal.min_qty}+ save {deal.percent_off}%
            </Badge>
          )}
        </div>
        {outOfStock ? (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <span className="font-display font-bold tracking-widest text-sm uppercase">
              Out of Stock
            </span>
          </div>
        ) : (
          <button
            onClick={handleQuickAdd}
            className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-[#0A1C40] text-white text-xs font-bold pl-3 pr-4 py-2 hover:bg-[#FFC220] hover:text-[#0A1C40] transition-colors shadow-md"
            data-testid={`quick-add-${product.id}`}
            aria-label={colorCount > 0 ? "Choose options" : "Add to cart"}
          >
            <Plus className="w-3.5 h-3.5" /> {colorCount > 0 ? "Options" : "Add"}
          </button>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="font-display font-bold text-xl text-gray-900">
            {onSale && <span className="text-[#DC2626] text-sm font-bold mr-1">Now</span>}
            {fmtMoney(product.price)}
          </div>
          {onSale && (
            <div className="text-sm text-gray-500 line-through">
              {fmtMoney(product.compare_at_price)}
            </div>
          )}
        </div>
        {onSale && (
          <div className="text-xs font-bold text-green-700 mt-0.5">
            {product.gold_tag ? `Sells elsewhere for ${fmtMoney(product.compare_at_price)}` : `You save ${fmtMoney(saveAmt)}`}
          </div>
        )}
        <div className="mt-2 text-sm leading-snug line-clamp-2 text-gray-800 group-hover:text-[#0A1C40] group-hover:underline transition-colors">
          {product.name}
        </div>
        <div className="mt-auto pt-2 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] tracking-wider text-gray-500 uppercase font-semibold">
            {product.category}
          </span>
          {colorCount > 0 && (
            <span className="text-[10px] tracking-wider text-gray-500 uppercase">
              · {colorCount} color{colorCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
        {product.sku && (
          <div className="mt-1 text-[10px] tracking-wider text-gray-400 uppercase">
            SKU: {product.sku}
          </div>
        )}
      </div>
      {showModal && (
        <VideoPlayerModal
          src={videoUrl}
          title={product.name}
          poster={img ? fileUrl(img) : undefined}
          onClose={() => setShowModal(false)}
        />
      )}
    </Link>
  );
}
